import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle
} from '@repo/ui/dialog'
import { useTranslation } from '@/lib/i18n'
import { ShortcutHint } from '@/modules/shortcuts/components/shortcut-hint'
import {
	SHORTCUT_GROUPS,
	SHORTCUTS,
	type ShortcutId
} from '@/modules/shortcuts/lib/shortcuts'

/**
 * Every shortcut, grouped, read from the registry — so the list cannot drift
 * from what the keys do. All of them rather than only the ones this page
 * registered: the list is where somebody learns the keys, and a list that
 * changed page by page would teach them less.
 */
export function ShortcutsDialog(props: {
	onOpenChange: (open: boolean) => void
	open: boolean
}) {
	const { t } = useTranslation()
	const ids = Object.keys(SHORTCUTS) as ShortcutId[]

	return (
		<Dialog onOpenChange={props.onOpenChange} open={props.open}>
			{/*
			 * `finalFocus={false}`: closing this one moves focus nowhere.
			 *
			 * Every other dialog in the product is opened by a control, so
			 * returning the focus to it is returning the person where they
			 * were. This one has no control: `?` opens it from wherever the
			 * caret happens to be, usually nowhere at all. Base UI then falls
			 * back to the last element it saw focused, which after a `Cmd+K`
			 * is the search field — so reading the shortcut list put the caret
			 * in the search box, and every listing shortcut after that was
			 * swallowed, because only `search.focus` is declared `inFields`
			 * (`shortcuts.ts`). Measured, not deduced: the focus is on `BODY`
			 * before this dialog opens and on the combobox after it closes
			 * (OPSSTD-9385).
			 */}
			<DialogContent className='sm:max-w-lg' finalFocus={false}>
				<DialogHeader>
					<DialogTitle>
						{t('client:shortcuts.dialog.title')}
					</DialogTitle>
				</DialogHeader>
				{(
					Object.keys(
						SHORTCUT_GROUPS
					) as (keyof typeof SHORTCUT_GROUPS)[]
				).map((group) => (
					<section className='flex flex-col gap-2' key={group}>
						<h3 className='font-medium text-muted-foreground text-xs uppercase tracking-wide'>
							{t(SHORTCUT_GROUPS[group])}
						</h3>
						<dl className='flex flex-col gap-1.5'>
							{ids
								.filter((id) => SHORTCUTS[id].group === group)
								.map((id) => (
									<div
										className='flex items-center justify-between gap-4 text-sm'
										key={id}
									>
										<dt>{t(SHORTCUTS[id].label)}</dt>
										<dd>
											<ShortcutHint all id={id} />
										</dd>
									</div>
								))}
						</dl>
					</section>
				))}
			</DialogContent>
		</Dialog>
	)
}
